// useEditorialLights, shared dark/light state for The Edit
//
// The lights toggle lives in the masthead and must work on both /the-edit
// (index) and /the-edit/[slug] (article). Both pages mount their own
// component tree, so we sync state via:
//   • localStorage (persist across reloads + new tabs)
//   • a custom DOM event ('lwd:edit-lights') so the OTHER page's hook
//     instance picks up the change without re-reading localStorage
//
// CSS variables are written to <html> on every change. Components opt in
// by referencing var(--edit-bg) / var(--edit-text) etc. through the C
// theme object, no prop drilling required.

import { useEffect, useState, useCallback } from 'react';
import { DARK, LIGHT } from './editorialTheme.js';

const STORAGE_KEY = 'lwd:edit-lights-v2'; // bumped to v2 so old 'light' preference is abandoned
const EVENT_NAME  = 'lwd:edit-lights';

function readInitial() {
  if (typeof window === 'undefined') return false; // default: light (lights on)
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'light') return false;
    if (v === 'dark')  return true;
  } catch {}
  return false; // default: light (lights on)
}

function applyToRoot(isDark) {
  if (typeof document === 'undefined') return;
  const t = isDark ? DARK : LIGHT;
  const r = document.documentElement.style;
  r.setProperty('--edit-bg',      t.bg);
  r.setProperty('--edit-surface', t.surface);
  r.setProperty('--edit-card',    t.card);
  r.setProperty('--edit-text',    t.text);
  r.setProperty('--edit-body',    t.body);
  r.setProperty('--edit-muted',   t.muted);
  r.setProperty('--edit-subtle',  t.subtle);
  r.setProperty('--edit-border',  t.border);
  r.setProperty('--edit-border2', t.border2);
}

// Inject the crossfade rule once on first call. Adding the
// `.edit-theme-transitioning` class on <html> for ~250ms on every toggle
// makes the lights switch feel like a mood shift, not an instant flip.
// The rule is applied to all descendants but limited to colour properties
// so existing per-element transitions (hover opacity, etc.) stay untouched.
let crossfadeStyleInjected = false;
function ensureCrossfadeStyle() {
  if (crossfadeStyleInjected || typeof document === 'undefined') return;
  const tag = document.createElement('style');
  tag.setAttribute('data-edit-crossfade', '');
  tag.textContent = `
    .edit-theme-transitioning,
    .edit-theme-transitioning *:not(svg):not(path) {
      transition: background-color 0.25s ease,
                  color 0.25s ease,
                  border-color 0.25s ease,
                  fill 0.25s ease !important;
    }
  `;
  document.head.appendChild(tag);
  crossfadeStyleInjected = true;
}

function startCrossfade() {
  if (typeof document === 'undefined') return;
  ensureCrossfadeStyle();
  const html = document.documentElement;
  html.classList.add('edit-theme-transitioning');
  // Clear after the transition completes so it doesn't interfere with
  // unrelated colour changes (hover states, etc.) the rest of the time.
  window.setTimeout(() => {
    html.classList.remove('edit-theme-transitioning');
  }, 280);
}

export function useEditorialLights() {
  const [isDark, setIsDarkState] = useState(readInitial);

  // Apply on mount + whenever isDark changes locally.
  useEffect(() => { applyToRoot(isDark); }, [isDark]);

  // Listen for changes from other hook instances (other route mounted).
  // Trigger the crossfade here too so a toggle fired on one route smooths
  // the transition on every other listening hook in the same tab.
  useEffect(() => {
    const onChange = (e) => {
      const next = e?.detail?.isDark;
      if (typeof next === 'boolean') {
        startCrossfade();
        setIsDarkState(next);
      }
    };
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      startCrossfade();
      setIsDarkState(e.newValue !== 'light');
    };
    window.addEventListener(EVENT_NAME, onChange);
    window.addEventListener('storage',  onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onChange);
      window.removeEventListener('storage',  onStorage);
    };
  }, []);

  const setIsDark = useCallback((next) => {
    const value = typeof next === 'function' ? next(readInitial()) : next;
    // Kick the crossfade BEFORE applying the new theme so the transition
    // class is on <html> when the CSS variables flip. Without this the
    // colours snap on the first frame and the eased class only smooths
    // the second frame onwards.
    startCrossfade();
    setIsDarkState(value);
    try { window.localStorage.setItem(STORAGE_KEY, value ? 'dark' : 'light'); } catch {}
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { isDark: value } }));
    applyToRoot(value);
  }, []);

  const toggle = useCallback(() => setIsDark(d => !d), [setIsDark]);

  return [isDark, setIsDark, toggle];
}

// Theme object that resolves through CSS vars. Falls back to literals so
// SSR / first-paint (before useEffect runs) still has correct DARK colours.
export const C_VARS = {
  bg:      'var(--edit-bg, #070604)',
  surface: 'var(--edit-surface, #0e0c09)',
  card:    'var(--edit-card, #111009)',
  text:    'var(--edit-text, #f0ebe0)',
  body:    'var(--edit-body, rgba(240,235,224,0.86))',
  muted:   'var(--edit-muted, rgba(240,235,224,0.48))',
  subtle:  'var(--edit-subtle, rgba(240,235,224,0.28))',
  border:  'var(--edit-border, rgba(240,235,224,0.07))',
  border2: 'var(--edit-border2, rgba(240,235,224,0.13))',
};
